import { saveDraftObjects, remapItemObject, requireSavedObject } from "./saveDraftObjects";
// EditWork.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import GeneratedItemsList from "../helpers/GeneratedItemsList.js";
import createdItemApi from "../../data/api/createdItemApi";
import {
  Button,
  Nav,
  NavDropdown,
  Modal,
  Image as RBImage,
  Alert
} from "react-bootstrap";
import ScriptCaller from "../../calculation/scriptCaller";
import { nextDraftId } from "../../calculation/draftIdCounter.js";
import ModelViewer from "../../model/ModelViewer.js";
import ObjectViewer from "../../model/ObjectViewer.js";
import { duplicateWorkObject } from "./duplicateWorkObject";
import ColorPickerModal from "../modals/ColorPickerModal";
import { useParams } from "react-router-dom";
import {
  resolveDraftObject,
  deleteCreatedItem,
  deleteObject,
  addObject,
  updateObject,
  addCreatedItem
} from "../../data/store/actions/objectStoreFunctions";
import {
  getAllObjects,
  getCreatedItemsByObject,
  getClientById,
  getAllColors,
  getSettingById
} from "../../data/getters";
import processScript from "../../calculation/itemGenerator/processScript";
import scriptItemApi from "../../data/api/scriptItemApi";
import { getScriptItemsByScript } from "../../data/getters";
import store from "../../data/store/store";
import { useLanguage } from "../helpers/LanguageContext.js";
import { selectObject } from "../../data/store/actions/objectStoreFunctions";
import { updateWork } from "../../data/store/actions/workStoreFunctions";
import { saveWorkWithRelatedApi } from "../../data/api/workApi";
import objectApi from "../../data/api/objectApi";
import "./editWork.css";
import Loading from "../helpers/Loading.js";
import {
  assignDuplungDetails,
  collectRefIdsFromDefs,
  isDTag,
  normalizeItemPayload,
  parseRoom,
  parseSetting
} from "./editWorkUtil";

function isImportedObject(object) {
  const name =
    typeof object?.name === "string" ? object.name : object?.name?.name;
  return name?.startsWith("Import -") ?? false;
}

function sameOptionalId(left, right) {
  if (left == null || left === "") return right == null || right === "";
  return right != null && right !== "" && String(left) === String(right);
}

function itemMatchesSwapSource(item, source) {
  if (source === undefined) return false;
  if (source instanceof Set) {
    return source.has(item.itemId ?? item.tmpId);
  }

  const colorId = item.color?.colorId ?? item.colorId ?? null;
  if (source && typeof source === "object") {
    const millingId = item.milling?.millingId ?? item.millingId ?? null;
    return (
      sameOptionalId(colorId, source.colorId) &&
      sameOptionalId(millingId, source.millingId)
    );
  }

  return sameOptionalId(colorId, source);
}

function EditWork() {
  const dispatch = useDispatch();
  const works = useSelector((state) => state.works);
  const role = useSelector((state) => state.auth.user?.role);
  const { workId } = useParams();
  const [toRegenerate, setToRegenerate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("0");
  const [showForm, setShowForm] = useState(false);
  const [showModel, setShowModel] = useState(true);
  const [hiddenItems, setHiddenItems] = useState(new Set());
  const [pendingItemUpdates, setPendingItemUpdates] = useState(new Map());
  const [pendingItemDeletes, setPendingItemDeletes] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const saveInFlightRef = useRef(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [defsCache, setDefsCache] = useState(new Map());
  const suppressObjectUpdatesRef = useRef(false);
  const objects = useSelector(
    (state) => state.objects.filter((o) => o.work?.workId === Number(workId)),
    shallowEqual
  );

  const userObjects = React.useMemo(
    () => objects.filter((o) => !o.system),
    [objects]
  );

  const createdItems = useSelector(
    (state) =>
      state.createdItems.filter((item) => item.work?.workId === Number(workId)),
    shallowEqual
  );

  const hasDraftObjects = objects.some((o) => o.__isDraft || o.objectId < 0);
  const hasDraftItems = createdItems.some(
    (it) => it.__isDraft || it.itemId < 0
  );
  const hasUnsavedChanges =
    pendingItemUpdates.size > 0 ||
    pendingItemDeletes.size > 0 ||
    hasDraftObjects ||
    hasDraftItems;
  const toggleItemVisibility = (itemId) => {
    console.log("hiding item: ", itemId);
    setHiddenItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;

    //document.body.style.overflow = "hidden";
    document.body.style.height = "95dvh";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
    };
  }, []);

  const { t } = useLanguage();

  const [swapSourceColor, setSwapSourceColor] = useState(undefined);

  const effectiveCreatedItems = React.useMemo(() => {
    return createdItems
      .filter((it) => !pendingItemDeletes.has(it.itemId))
      .map((it) => {
        const upd = pendingItemUpdates.get(it.itemId);
        if (!upd) return it;
        let changed = false;
        for (const k in upd) {
          if (upd[k] !== it[k]) {
            changed = true;
            break;
          }
        }

        return changed ? { ...it, ...upd } : it;
      });
  }, [createdItems, pendingItemUpdates, pendingItemDeletes]);

  const importedObjectIds = React.useMemo(
    () =>
      new Set(
        objects
          .filter(isImportedObject)
          .map((object) => String(object.objectId))
      ),
    [objects]
  );
  const defaultCollapsedObjectIds = React.useMemo(
    () => Array.from(importedObjectIds),
    [importedObjectIds]
  );
  const [palette, setPalette] = useState([]);
  const [collapsedColors, setCollapsedColors] = useState({});
  const [currentObject, setCurrentObject] = useState([]);
  const [settingDetails, setSettingDetails] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [objectToDelete, setObjectToDelete] = useState(null);
  const [localWork, setLocalWork] = useState(null);
  const [showPaletteModal, setShowPaletteModal] = useState(false);
  const isOrdered = localWork?.isOrdered; //
  const canAddObject =
    !isOrdered || ["admin", "companyAdmin", "companyUser"].includes(role);
  const colors = dispatch(useSelector(getAllColors)) || [];
  const millings = useSelector((state) => state.millings || []);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [panelWidth, setPanelWidth] = useState(30);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (!saveMessage) return;

    const t = setTimeout(() => {
      setSaveMessage(null);
    }, 3000);

    return () => clearTimeout(t);
  }, [saveMessage]);

  const getDefs = useCallback(
    async (sid) => {
      if (defsCache.has(sid)) return defsCache.get(sid);

      let arr = await scriptItemApi.getAllScriptItemsForScriptApi(sid);
      if (!Array.isArray(arr) || arr.length === 0) {
        try {
          await dispatch(getScriptItemsByScript(sid));
        } catch {}
        const state = store.getState();
        const all = state?.scriptItems || [];
        arr = all.filter((d) => String(d.scriptId) === String(sid));
      }
      const defs = Array.isArray(arr) ? arr : [];
      setDefsCache((prev) => {
        const m = new Map(prev);
        m.set(sid, defs);
        return m;
      });
      return defs;
    },
    [defsCache, dispatch]
  );

  const buildAllDefsForConfig = useCallback(
    async (rootScriptId, cfg) => {
      const seen = new Set();
      const queue = [Number(rootScriptId)];
      let bundle = [];
      while (queue.length) {
        const sid = queue.shift();
        if (seen.has(sid)) continue;
        seen.add(sid);
        const defs = await getDefs(sid);
        bundle.push(...defs);
        const refIds = collectRefIdsFromDefs(defs, cfg);
        refIds.forEach((id) => {
          if (!seen.has(id)) queue.push(id);
        });
      }
      return bundle;
    },
    [getDefs]
  );

  const handleRegenerate = useCallback(
    async (obj) => {
      const fullConfig =
        typeof obj.setting === "string"
          ? JSON.parse(obj.setting)
          : (obj.setting ?? {});

      const { width, height, depth, ...otherConfig } = fullConfig;
      const dims = { width, height, depth };
      const cfg = { ...otherConfig, width, height, depth };

      const scriptId = obj.usedScript?.scriptId;
      if (!scriptId) {
        console.warn("Nincs scriptId:", obj);
        return;
      }

      const allDefs = await buildAllDefsForConfig(scriptId, cfg);
      const newItems = processScript(cfg, dims, scriptId, allDefs);

      const oldItems = createdItems.filter(
        (it) => it.object?.objectId === obj.objectId
      );

      function sizeKey(s) {
        if (!s) return "";
        if (Array.isArray(s)) return s.join(",");
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr)) return arr.join(",");
        } catch {}
        return String(s);
      }

      // name+size alapján mentjük a régi színeket
      const oldColorMap = new Map();
      oldItems.forEach((it) => {
        const key = `${it.name ?? ""}|${sizeKey(it.size)}`;
        const cid = it.color?.colorId ?? it.colorId ?? null;
        if (!oldColorMap.has(key)) oldColorMap.set(key, cid);
      });

      oldItems.forEach((it) => dispatch(deleteCreatedItem(it.itemId)));

      newItems.forEach((it, i) => {
        const key = `${it.name ?? ""}|${sizeKey(it.size)}`;
        const preservedColorId = oldColorMap.has(key)
          ? oldColorMap.get(key)
          : null;
        const resolvedColor =
          it.colorId === -1 || it.material?.toUpperCase?.() === "PFL"
            ? { colorId: -1 }
            : preservedColorId != null
              ? { colorId: preservedColorId }
              : (it.color ?? null);

        dispatch(
          addCreatedItem({
            ...it,
            itemId: nextDraftId(),
            __isDraft: true,
            object: { objectId: obj.objectId },
            work: { workId: +workId },
            color: resolvedColor,
            size: Array.isArray(it.size)
              ? JSON.stringify(it.size)
              : (it.size ?? "[0,0,0]"),
            position: Array.isArray(it.position)
              ? JSON.stringify(it.position)
              : (it.position ?? "[0,0,0]"),
            rotation: Array.isArray(it.rotation)
              ? JSON.stringify(it.rotation)
              : (it.rotation ?? "[0,0,0]")
          })
        );
      });

      dispatch(
        updateObject({
          ...obj,
          setting:
            typeof obj.setting === "string"
              ? obj.setting
              : JSON.stringify(obj.setting)
        })
      );
    },
    [dispatch, createdItems, workId, buildAllDefsForConfig]
  );
  const handleSettingChange = useCallback(
    (oid, newSettingObj) => {
      const obj = objects.find((o) => String(o.objectId) === String(oid));
      if (!obj) return;
      dispatch(
        updateObject({ ...obj, setting: JSON.stringify(newSettingObj) })
      );
    },
    [objects, dispatch]
  );
  // load work and objects
  useEffect(() => {
    const initWork = async () => {
      const orig = works.find((w) => w.workId === +workId);
      console.log("orig: ", orig);
      if (!orig) {
        setLoading(false);
        return;
      }
      await dispatch(getClientById(orig.ClientId));
      const today = new Date();
      setLocalWork({
        ...orig,
        Date: today.toISOString().slice(0, 10),
        Status: "Pending",
        objects: orig.objects || []
      });
      setLoading(false);
    };
    initWork();
  }, [works, workId, dispatch]);

  const handleRoomChange = (axis, value) => {
    const num = parseFloat(value) || 0;
    const old = parseRoom(localWork.room);
    const next = { ...old, [axis]: num };
    // összeállítjuk a mm‑es stringet
    const roomStr = `[${next.h},${next.w},${next.d}]`;
    // 1) frissítsd a helyi stétet
    setLocalWork((w) => ({ ...w, room: roomStr }));
    // 2) küldjed el a backendre
    dispatch(updateWork({ ...localWork, room: roomStr }));
  };

  const topPaletteByMaterial = React.useMemo(() => {
    const usedColorIds = new Set(
      effectiveCreatedItems.map((it) => it.color?.colorId ?? it.colorId ?? null)
    );
    const millingsByColor = new Map();
    const unmilledColorIds = new Set();
    effectiveCreatedItems.forEach((item) => {
      const colorId = item.color?.colorId ?? item.colorId ?? null;
      const millingId = item.milling?.millingId ?? item.millingId;
      if (millingId == null || millingId === "") {
        unmilledColorIds.add(colorId);
        return;
      }

      const millingName =
        item.milling?.name ??
        millings.find(
          (milling) => String(milling.millingId) === String(millingId)
        )?.name ??
        String(millingId);

      const entries = millingsByColor.get(colorId) || new Map();
      entries.set(String(millingId), { millingId, name: millingName });
      millingsByColor.set(colorId, entries);
    });
    const groups = new Map();

    if (usedColorIds.has(null)) {
      const entries = Array.from(millingsByColor.get(null)?.values() || []).map(
        ({ millingId, name }) => ({
          colorId: null,
          name: t("noColor"),
          millingId,
          millingNames: [name],
          isNoColor: true
        })
      );
      if (unmilledColorIds.has(null)) {
        entries.push({
          colorId: null,
          name: t("noColor"),
          isUnmilled: true,
          millingNames: [],
          isNoColor: true
        });
      }
      groups.set("Nincs szin", entries);
    }

    palette
      .filter((c) => usedColorIds.has(c.colorId))
      .forEach((c) => {
        const mat = c.materialType || "Alap";
        if (!groups.has(mat)) groups.set(mat, []);
        const entries = Array.from(
          millingsByColor.get(c.colorId)?.values() || []
        );
        const hasUnmilledItems = unmilledColorIds.has(c.colorId);
        entries.forEach(({ millingId, name }) =>
          groups.get(mat).push({
            ...c,
            millingId,
            millingNames: [name]
          })
        );
        if (hasUnmilledItems) {
          groups.get(mat).push({ ...c, isUnmilled: true, millingNames: [] });
        }
      });

    const knownColorIds = new Set(colors.map((color) => String(color.colorId)));
    const unknownColorIds = Array.from(usedColorIds).filter(
      (colorId) =>
        colorId != null &&
        String(colorId) !== "-1" &&
        !knownColorIds.has(String(colorId))
    );
    const unknownColors = [];
    unknownColorIds.forEach((colorId) => {
      const entries = Array.from(millingsByColor.get(colorId)?.values() || []);
      const hasUnmilledItems = unmilledColorIds.has(colorId);
      entries.forEach(({ millingId, name }) =>
        unknownColors.push({
          colorId,
          millingId,
          isUnknownColor: true,
          millingNames: [name]
        })
      );
      if (hasUnmilledItems) {
        unknownColors.push({
          colorId,
          isUnknownColor: true,
          isUnmilled: true,
          millingNames: []
        });
      }
    });
    if (unknownColors.length) {
      groups.set(t("unknownColor"), unknownColors);
    }

    return Array.from(groups.entries()).map(([material, colors]) => ({
      material,
      colors
    }));
  }, [palette, effectiveCreatedItems, millings, colors, t]);

  useEffect(() => {
    const wid = +workId;

    const loadItems = async () => {
      if (selectedTab === "newObject") {
        //  setCreatedItems([]);
        return;
      }

      try {
        let all = [];
        if (selectedTab === "0") {
          const results = await Promise.all(
            objects.map((o) => dispatch(getCreatedItemsByObject(o.objectId)))
          );
          all = results.flat().filter((it) => it.work?.workId === wid);
        } else {
          const oid = +selectedTab;
          const items = await dispatch(getCreatedItemsByObject(oid));
          all = items.filter((it) => it.work?.workId === wid);
        }
      } catch (err) {
        console.error("Failed to load createdItems:", err);
      }
    };

    loadItems();
  }, [objects, selectedTab, workId, dispatch]);

  // update palette on createdItems change

  const PFL_COLOR = useSelector((state) =>
    state.colors.find((c) => c.colorId === -1)
  ) || {
    colorId: -1,
    name: "PFL",
    hex: "#FFFFFF",
    imageDataReduced: "",
    imageData: ""
  };

  useEffect(() => {
    const realUsedColorIds = new Set(
      effectiveCreatedItems
        .map((it) => it.color?.colorId)
        .filter((cid) => typeof cid === "number" && cid !== -1)
    );
    const usedRealColors = Array.from(realUsedColorIds)
      .map((id) => colors.find((c) => c.colorId === id))
      .filter(Boolean);
    const newPalette = [PFL_COLOR, ...usedRealColors];
    setPalette(newPalette);
  }, [effectiveCreatedItems, colors]);

  const getEffectiveItemByIndex = (index) => {
    const it = effectiveCreatedItems[index];
    if (!it) return null;
    return it;
  };

  const handleCreatedItemChange = (index, fields) => {
    const orig = getEffectiveItemByIndex(index);
    if (!orig) return;

    const base = {
      itemId: orig.itemId,
      name: orig.name,
      details: orig.details,
      material: orig.material,
      kant: orig.kant,
      qty: orig.qty,
      position: orig.position,
      rotation: orig.rotation,
      size: orig.size,
      tablePosition: orig.tablePosition,
      tableRotation: orig.tableRotation,
      object: { objectId: orig.object.objectId },
      work: { workId: orig.work.workId },
      rotable: orig.rotable ?? true,
      milling: orig.milling
    };

    const normalized = normalizeItemPayload({ ...base, ...fields });

    console.log("LOCAL UPDATE:", {
      itemId: orig.itemId,
      before: orig,
      update: fields,
      normalized
    });
    setPendingItemUpdates((prev) => {
      const next = new Map(prev);
      next.set(orig.itemId, normalized);
      return next;
    });
  };

  // ── Új item hozzáadása ───────────────────────────────────
  const handleAddItem = () => {
    if (isOrdered) return;
    // csak ha tényleg egy objektum van kijelölve (selectedTab !== "0" és !== "newObject")
    if (selectedTab !== "0" && selectedTab !== "newObject") {
      const defaultItem = {
        size: "[1,1,18]",
        qty: 1,
        position: "[0,0,0]",
        rotation: "[0,0,0]",
        color: { colorId: -1 },
        name: "",
        details: "",
        material: "", // anyag
        rotable: true,
        object: { objectId: +selectedTab },
        work: { workId: +workId }, // ← ide kell a munka
        itemId: nextDraftId()
      };
    }
  };

  const handleModelViewerUpdate = (updatedItem, groupIdx) => {
    const idx = createdItems.findIndex(
      (it) => it.itemId === updatedItem.itemId
    );
    if (idx !== -1) {
      handleCreatedItemChange(idx, {
        color: { colorId: updatedItem?.colorId },
        position: updatedItem.position,
        rotation: updatedItem.rotation
      });
    }
  };

  const handleObjectUpdate = (updatedObject, objectIndex) => {
    dispatch(updateObject(updatedObject));
    /*console.log("[EditWork] handleObjectUpdate()", {
      updatedObject,
      objectIndex
    });

    if (updatedObject.__isDraft || updatedObject.objectId < 0) {
      dispatch(updateObject(updatedObject));
      return;
    }

    dispatch(objectApi.updateObjectApi(updatedObject.objectId, updatedObject))
      .then(() =>
        console.log("[EditWork] API sikeres object update:", updatedObject)
      )
      .catch((err) => console.error("[EditWork] Error updating object:", err));*/
  };

  const handleObjectViewerUpdate = (patch, groupIdx) => {
    const idx = createdItems.findIndex((it) => it.itemId === patch.itemId);
    if (idx === -1) return;

    handleCreatedItemChange(idx, patch);
  };

  const handleSelectTab = (key) => {
    if (key === "newObject" && !canAddObject) return;
    setSelectedTab(key);
    dispatch(selectObject(key));
    if (key === "0") {
      setShowForm(false);
      setShowModel(true);
    } else if (key === "newObject") {
      setShowForm(true);
      setShowModel(false);
    } else {
      setShowForm(false);
      setShowModel(false);
    }
  };
  useEffect(() => {
    if (selectedTab === "0") setShowModel(true);
    else setShowModel(false);
    // and likewise for showForm:
    setShowForm(selectedTab === "newObject");
  }, [selectedTab]);
  const handleSaveWork = async () => {
    if (!localWork || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setIsSaving(true);
    setLoading(true);
    try {
      const objectIdMap = await saveDraftObjects(objects,
        payload => dispatch(objectApi.createObjectApi(payload)),
        (draft, saved) => {
          dispatch(resolveDraftObject(draft.objectId, saved));
          setPendingItemUpdates(previous => new Map(Array.from(previous, ([id, item]) =>
            [id, remapItemObject(item, { [draft.objectId]: saved.objectId })])));
          if (String(selectedTab) === String(draft.objectId)) handleSelectTab(String(saved.objectId));
        });
      const remap = item => requireSavedObject(remapItemObject(item, objectIdMap));
      // Validate the entire outgoing set before issuing any item writes.
      const updates = Array.from(pendingItemUpdates.values())
        .filter(item => item.itemId > 0 && !pendingItemDeletes.has(item.itemId))
        .map(remap);
      const unsaved = assignDuplungDetails(createdItems,
        effectiveCreatedItems.filter(item => item.itemId < 0 && !item.frontend)).map(remap);
      if (updates.length) {
        await dispatch(createdItemApi.updateMultipleCreatedItemsApi(updates));
        setPendingItemUpdates(previous => {
          const next = new Map(previous);
          updates.forEach(item => next.delete(item.itemId));
          return next;
        });
      }
      for (const id of pendingItemDeletes) {
        if (Number(id) > 0) await dispatch(createdItemApi.deleteCreatedItemApi(id));
        else dispatch(deleteCreatedItem(id));
        setPendingItemDeletes(previous => {
          const next = new Set(previous); next.delete(id); return next;
        });
      }
      if (unsaved.length) {
        const payloads = unsaved.map(item => {
          const payload = normalizeItemPayload({ ...item,
            object: item.object ? { objectId: item.object.objectId } : null,
            work: { workId: +workId },
            color: item.color?.colorId != null ? { colorId: item.color.colorId } : null,
            material: item.material ?? '' });
          delete payload.itemId;
          return payload;
        });
        const savedItems = await dispatch(createdItemApi.createMultipleCreatedItemsApi(payloads));
        unsaved.forEach(item => dispatch(deleteCreatedItem(item.itemId)));
        savedItems.forEach(item => dispatch(addCreatedItem(item)));
        setPendingItemUpdates(previous => {
          const next = new Map(previous);
          unsaved.forEach(item => next.delete(item.itemId));
          return next;
        });
      }
      await dispatch(saveWorkWithRelatedApi(workId, localWork));
      await Promise.all(objects.map(object =>
        dispatch(getCreatedItemsByObject(objectIdMap[object.objectId] ?? object.objectId))));
      setSaveMessage({ type: 'success', text: t('saveSuccess') });
    } catch (error) {
      const detail = typeof error.response?.data === 'string' ? error.response.data : error.message;
      setSaveMessage({ type: 'danger', text: `${t('saveFailed')}${detail ? `: ${detail}` : ''}` });
    } finally {
      setLoading(false);
      setIsSaving(false);
      saveInFlightRef.current = false;
    }
  };

  const handleDeleteObject = (id) => {
    if (isOrdered) return;
    setObjectToDelete(id);
    setShowDeleteModal(true);
  };
  const handleDuplicateObject = (id) => {
    if (isOrdered || isSaving) return;
    const source = objects.find((o) => String(o.objectId) === String(id));
    const result = duplicateWorkObject(source, effectiveCreatedItems, objects);
    if (!result) return;
    dispatch(addObject(result.object));
    result.items.forEach((item) => dispatch(addCreatedItem(item)));
    handleSelectTab(String(result.object.objectId));
    setSaveMessage({
      type: "info",
      text: "Az objektum és elemei lemásolva. A véglegesítéshez kattints a Mentés gombra."
    });
  };

  const confirmDeleteObject = async () => {
    if (objectToDelete == null) return;
    const obj = objects.find((o) => o.objectId === objectToDelete);
    if (!obj) return;
    suppressObjectUpdatesRef.current = true;
    const isDraft = obj.__isDraft === true || obj.objectId < 0;
    if (isDraft) {
      const relatedItems = createdItems.filter(
        (it) => it.object?.objectId === obj.objectId
      );
      relatedItems.forEach((it) => {
        dispatch(deleteCreatedItem(it.itemId));
      });
      dispatch(deleteObject(obj.objectId));
    } else {
      await dispatch(objectApi.deleteObjectApi(obj.objectId));
      const relatedItems = createdItems.filter(
        (it) => it.object?.objectId === obj.objectId
      );
      relatedItems.forEach((it) => {
        dispatch(deleteCreatedItem(it.itemId));
      });
    }
    dispatch(selectObject("0"));
    setSelectedTab("0");
    const all = await dispatch(getAllObjects());
    setLocalWork((w) => ({
      ...w,
      objects: all.filter((o) => o.work?.workId === w.workId)
    }));
    setShowDeleteModal(false);
    setObjectToDelete(null);
    suppressObjectUpdatesRef.current = false;
  };
  const handleDeleteItem = (index) => {
    const item = effectiveCreatedItems[index];
    if (!item) return;
    setPendingItemDeletes((prev) => {
      if (prev.has(item.itemId)) return prev;
      const next = new Set(prev);
      next.add(item.itemId);
      return next;
    });
  };

  // UI helpers for palette modal
  const openPalette = (...args) => {
    if (args.length > 0) {
      const [sourceColor] = args;
      setSwapSourceColor(sourceColor);
    }
    setShowPaletteModal(true);
  };
  const closePalette = () => {
    setShowPaletteModal(false);
    setSwapSourceColor(undefined);
  };

  const selectColor = (c) => {
    if (swapSourceColor !== undefined) {
      if (swapSourceColor instanceof Set) {
        // Item-szintű szín változtatás (ColorSwatch kattintásból jött)
        swapSourceColor.forEach((itemId) => {
          const idx = effectiveCreatedItems.findIndex(
            (it) => (it.itemId ?? it.tmpId) === itemId
          );
          if (idx >= 0) {
            handleCreatedItemChange(idx, { color: { colorId: c.colorId } });
          }
        });
      } else {
        // Limit bulk swaps to the selected color and milling pair.
        const bulkUpdates = effectiveCreatedItems
          .filter((it) => itemMatchesSwapSource(it, swapSourceColor))
          .map((it) =>
            normalizeItemPayload({ ...it, color: { colorId: c.colorId } })
          );

        setPendingItemUpdates((prev) => {
          const next = new Map(prev);
          bulkUpdates.forEach((p) => next.set(p.itemId, p));
          return next;
        });
      }
      setSwapSourceColor(undefined);
      closePalette();
    } else {
      setPalette((p) =>
        c.colorId != null &&
        p.length < 5 &&
        !p.some((x) => x.colorId === c.colorId)
          ? [...p, c]
          : p
      );
      closePalette();
    }
  };

  const removeColor = (cid) =>
    setPalette((p) => p.filter((x) => x.colorId !== cid));

  // layout panel resizing
  const onMouseDown = (e) => {
    startXRef.current = e.clientX;
    startWidthRef.current = panelWidth;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  const onMouseMove = (e) => {
    const dx = e.clientX - startXRef.current;
    const newW = Math.min(
      40,
      Math.max(15, startWidthRef.current - (dx / window.innerWidth) * 100)
    );
    setPanelWidth(newW);
  };
  const onMouseUp = () => {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };
  const memoizedObjects = React.useMemo(() => objects, [objects]);

  const memoizedItems = React.useMemo(
    () => [...effectiveCreatedItems],
    [effectiveCreatedItems]
  );
  const memoizedPalette = React.useMemo(() => palette, [palette]);

  const memoizedRoom = React.useMemo(
    () => parseRoom(localWork?.room || "[2500,5000,5000]"),
    [localWork?.room]
  );
  const paletteSourceItem = effectiveCreatedItems.find((item) =>
    itemMatchesSwapSource(item, swapSourceColor)
  );
  const pickerColorId =
    swapSourceColor &&
    typeof swapSourceColor === "object" &&
    !(swapSourceColor instanceof Set)
      ? swapSourceColor.colorId
      : swapSourceColor instanceof Set
        ? (paletteSourceItem?.color?.colorId ?? null)
        : swapSourceColor;
  const pickerMillingId =
    paletteSourceItem?.milling?.millingId ??
    paletteSourceItem?.millingId ??
    null;
  const pickerMillingVariantId = paletteSourceItem?.millingVariantId ?? "";
  if (isSaving) {
    return (
      <div
        style={{
          height: "95dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Loading text={t("savingInProgress")} />
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "95dvh", width: "100%" }}>
      <ColorPickerModal
        show={showPaletteModal && (!isOrdered || (canAddObject && selectedTab === "newObject"))}
        colors={colors}
        onClose={closePalette}
        onSelect={selectColor}
        millings={millings}
        colorId={pickerColorId}
        millingId={pickerMillingId}
        millingVariantId={pickerMillingVariantId}
        onSelectMilling={
          swapSourceColor === undefined
            ? undefined
            : (milling, variantId, selectedColor) => {
                effectiveCreatedItems.forEach((item, index) => {
                  const matches = itemMatchesSwapSource(item, swapSourceColor);
                  if (matches)
                    handleCreatedItemChange(index, {
                      ...(selectedColor
                        ? { color: { colorId: selectedColor.colorId } }
                        : {}),
                      millingId: milling?.millingId ?? null,
                      millingVariantId: variantId || ""
                    });
                });
              }
        }
      />

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("deleteConfirmTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("deleteConfirmBody")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("cancelBtn")}
          </Button>
          <Button variant="danger" onClick={confirmDeleteObject}>
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {saveMessage && (
        <Alert variant={saveMessage.type} className="m-2 save-message-alert">
          {saveMessage.text}
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Nav
        variant="tabs"
        activeKey={selectedTab}
        onSelect={handleSelectTab}
        className="mb-2 edit-work-nav"
      >
        {canAddObject && (
          <Nav.Item>
            <Nav.Link
              eventKey="newObject"
              onClick={() => {
                setShowForm(true);
                setShowModel(false);
              }}
            >
              {t("addNewObject")}
            </Nav.Link>
          </Nav.Item>
        )}
        <Nav.Item>
          <Nav.Link eventKey="0" onClick={() => setShowForm(false)}>
            {t("model")}
          </Nav.Link>
        </Nav.Item>
        <NavDropdown
          style={{ width: "100px" }}
          title={
            selectedTab === "0"
              ? t("objects")
              : userObjects.find((o) => o.objectId.toString() === selectedTab)
                  ?.name?.name || t("object")
          }
          id="objects-dropdown"
        >
          {canAddObject && (
            <NavDropdown.Item
              eventKey="newObject"
              onClick={() => {
                setShowForm(true);
                setShowModel(false);
              }}
            >
              {t("addNewObject")}
            </NavDropdown.Item>
          )}
          <NavDropdown.Divider />
          {userObjects.map((o) => (
            <NavDropdown.Item key={o.objectId} eventKey={o.objectId.toString()}>
              {o.objectId} | {t(o.name)}
              {!isOrdered && (
                <Button
                  variant="light"
                  size="sm"
                  disabled={isSaving}
                  title="Objektum duplikálása az elemeivel"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDuplicateObject(o.objectId);
                  }}
                >
                  Duplikálás
                </Button>
              )}
              {!isOrdered && (
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteObject(o.objectId);
                  }}
                  style={{ marginLeft: 8, padding: "2px 6px" }}
                >
                  x
                </Button>
              )}
            </NavDropdown.Item>
          ))}
        </NavDropdown>

        <div
          className="d-flex align-items-center ms-auto"
          style={{ gap: "1rem", marginRight: "1rem" }}
        >
          {topPaletteByMaterial.map(({ material, colors }) => (
            <div
              key={material}
              className="d-flex align-items-center"
              style={{ gap: "0.25rem", whiteSpace: "nowrap" }}
            >
              <div
                style={{
                  fontSize: ".75rem",
                  fontWeight: "500"
                }}
              >
                {material}:
              </div>
              <div className="d-flex" style={{ gap: "0.25rem" }}>
                {colors.map((c) => (
                  <button
                    type="button"
                    key={`${c.isNoColor ? "no-color" : c.isUnknownColor ? "unknown" : "color"}-${c.colorId ?? "none"}-${c.millingId ?? (c.isUnmilled ? "unmilled" : "unspecified")}`}
                    className="edit-work-color-milling"
                    title={`${c.isNoColor ? t("noColor") : c.isUnknownColor ? `${t("unknownColor")} #${c.colorId}` : t(c.name)}${c.isUnmilled ? ` | ${t("noMilling1")}` : c.millingNames?.length ? ` | ${c.millingNames.map((name) => t(name)).join(", ")}` : ""}`}
                    onClick={() =>
                      openPalette({
                        colorId: c.colorId ?? null,
                        millingId: c.millingId ?? null
                      })
                    }
                  >
                    {c.isNoColor ? (
                      <div
                        className="edit-work-color-milling__image edit-work-color-milling__no-color"
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "relative"
                        }}
                      >
                        <svg
                          viewBox="0 0 10 10"
                          className="edit-work-color-milling__no-color-mark"
                        >
                          <line
                            x1="2"
                            y1="2"
                            x2="8"
                            y2="8"
                            stroke="#dc3545"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <line
                            x1="8"
                            y1="2"
                            x2="2"
                            y2="8"
                            stroke="#dc3545"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    ) : c.isUnknownColor ? (
                      <span className="edit-work-color-milling__image edit-work-color-milling__unknown">
                        ?
                      </span>
                    ) : c.imageDataReduced ? (
                      <RBImage
                        src={`data:image/jpeg;base64,${c.imageDataReduced}`}
                        className="edit-work-color-milling__image"
                      />
                    ) : (
                      <div
                        className="edit-work-color-milling__image"
                        style={{
                          backgroundColor: c.hex || "#ddd"
                        }}
                      />
                    )}
                    {(c.isUnmilled || c.millingNames?.length > 0) && (
                      <>
                        <span
                          className="edit-work-color-milling__separator"
                          aria-hidden="true"
                        >
                          |
                        </span>
                        <span className="edit-work-color-milling__name">
                          {c.isUnmilled
                            ? t("noMilling1")
                            : c.millingNames.map((name) => t(name)).join(", ")}
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={handleSaveWork}
          variant={hasUnsavedChanges ? "primary" : "secondary"}
          disabled={!hasUnsavedChanges || isSaving}
        >
          {t("save")}
        </Button>
      </Nav>

      {/* Main Content */}

      <div className="d-flex" style={{ height: "85dvh", overflow: "hidden" }}>
        {/* Left Panel */}
        <div
          style={{
            flex: "1 1 0", // növekedjék és zsugorodjék is
            minWidth: 0, // engedi, hogy a gyerek elemek befelé zsugorodjanak
            overflowY: "hidden"
          }}
        >
          {(selectedTab === "0" || selectedTab === "newObject") &&
            showModel && (
              <ModelViewer
                objects={memoizedObjects}
                createdItems={memoizedItems} // már átadod
                usedColors={memoizedPalette}
                effectiveCreatedItems={memoizedItems} // ← új
                palette={memoizedPalette} // ← új
                hiddenItems={hiddenItems}
                setHiddenItems={setHiddenItems} // ← új prop
                onItemUpdate={handleModelViewerUpdate}
                onObjectUpdate={handleObjectUpdate}
                roomSize={memoizedRoom}
                onRoomSizeChange={handleRoomChange}
                /*key={`model-${objects
                  .map((o) => o.objectId)
                  .join(",")}-${effectiveCreatedItems
                  .map((i) => i.itemId)
                  .join(",")}`}*/
              />
            )}
          {canAddObject && selectedTab === "newObject" && showForm && (
            <div
              style={{
                flex: "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                height: "85vh",
                overflow: "hidden"
              }}
            >
              {toRegenerate ? (
                <ScriptCaller
                  initialObject={toRegenerate.originalObject}
                  initialScriptId={toRegenerate.scriptId}
                  initialConfig={toRegenerate.config}
                  initialDims={toRegenerate.dims}
                  initialGeneratedItems={toRegenerate.items}
                  onSave={() => {
                    setToRegenerate(null);
                    setSelectedTab("0");
                  }}
                  palette={palette}
                  onColorSelect={selectColor}
                  onColorRemove={removeColor}
                  showPaletteModal={showPaletteModal}
                  openPaletteModal={openPalette}
                  closePaletteModal={closePalette}
                  style={{ flex: 1 }}
                />
              ) : (
                <ScriptCaller
                  newObjectKey="new"
                  onSave={async (generatedItems) => {
                    setShowForm(false);
                    setShowModel(true);
                    setSelectedTab("0");
                  }}
                  palette={palette}
                  onColorSelect={selectColor}
                  onColorRemove={removeColor}
                  showPaletteModal={showPaletteModal}
                  openPaletteModal={openPalette}
                  closePaletteModal={closePalette}
                  style={{ flex: 1 }}
                />
              )}
            </div>
          )}
          {selectedTab !== "0" && selectedTab !== "newObject" && (
            <ObjectViewer
              object={objects.find((o) => o.objectId === +selectedTab)}
              createdItems={effectiveCreatedItems.filter(
                (it) => it.object.objectId === +selectedTab
              )}
              usedColors={palette}
              onItemUpdate={handleObjectViewerUpdate}
            />
          )}
        </div>

        {/* Splitter + Right Panel */}
        {!showForm && selectedTab !== "newObject" && (
          <>
            <div onMouseDown={onMouseDown} className="edit-work-splitter" />

            <div
              className={`edit-work-right-panel${collapsed ? " is-collapsed" : ""}`}
              style={{ "--panel-width": `${panelWidth}vw` }}
            >
              {!collapsed && (
                <>
                  {selectedTab !== "0" && (
                    <>
                      <h3>{t("settingsTitle")}</h3>
                      {currentObject.map((o) => (
                        <div key={o.objectId}>
                          <h4
                            onClick={() =>
                              setSettingDetails((sd) =>
                                sd.includes(o.objectId)
                                  ? sd.filter((id) => id !== o.objectId)
                                  : [...sd, o.objectId]
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {o.name}
                          </h4>
                          {settingDetails.includes(o.objectId) &&
                            Object.entries(parseSetting(o.setting)).map(
                              ([k, v]) => (
                                <p key={k}>
                                  {dispatch(getSettingById(k))?.name}: {v}
                                </p>
                              )
                            )}
                        </div>
                      ))}
                    </>
                  )}
                  <h3 className="d-flex align-items-center">
                    {t("createdItemsTitle")}
                    {selectedTab !== "0" &&
                      selectedTab !== "newObject" &&
                      !isOrdered && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="ms-2"
                          onClick={handleAddItem}
                        >
                          {t("addNewItem")}
                        </Button>
                      )}
                  </h3>
                  <div style={{ maxHeight: "75vh", overflowY: "auto" }}>
                    <GeneratedItemsList
                      calledFrom={1}
                      onRegenerate={(oid, newSettingObj) => {
                        const obj = objects.find(
                          (o) => String(o.objectId) === String(oid)
                        );
                        if (!obj) return;
                        handleRegenerate({
                          ...obj,
                          setting: JSON.stringify(newSettingObj)
                        });
                      }}
                      onDeleteObject={handleDeleteObject}
                      onDuplicateObject={
                        isSaving ? undefined : handleDuplicateObject
                      }
                      onSettingChange={handleSettingChange}
                      generatedItems={effectiveCreatedItems}
                      defaultCollapsedObjectIds={defaultCollapsedObjectIds}
                      defaultHiddenObjectIds={defaultCollapsedObjectIds}
                      palette={palette}
                      collapsedColors={collapsedColors}
                      onSwap={(colorId) => {
                        setSwapSourceColor(colorId);
                        setShowPaletteModal(true);
                      }}
                      toggleColor={(cid) =>
                        setCollapsedColors((cc) => ({ ...cc, [cid]: !cc[cid] }))
                      }
                      readOnly={isOrdered ? isOrdered : false}
                      handleItemChange={handleCreatedItemChange}
                      onItemColorSelect={(itemIds, colorId) => {
                        if (colorId === null) {
                          setSwapSourceColor(itemIds);
                          openPalette();
                        } else {
                          itemIds.forEach((itemId) => {
                            const idx = effectiveCreatedItems.findIndex(
                              (it) => (it.itemId ?? it.tmpId) === itemId
                            );
                            if (idx >= 0) {
                              handleCreatedItemChange(idx, {
                                color: { colorId }
                              });
                            }
                          });
                        }
                      }}
                      handleItemColorChange={(idx, color) =>
                        handleCreatedItemChange(idx, {
                          color: { colorId: color.colorId }
                        })
                      }
                      onDragEnd={(result) => {
                        const { destination, draggableId } = result;
                        if (!destination) return;
                        const m = String(draggableId).match(/^item-(-?\d+)$/);
                        const idNum = m ? Number(m[1]) : Number(draggableId);
                        if (!Number.isFinite(idNum)) return;
                        const idx = effectiveCreatedItems.findIndex(
                          (it) =>
                            Number(it.itemId) === idNum ||
                            Number(it.tmpId) === idNum
                        );
                        if (idx < 0) return;
                        const newCid =
                          destination.droppableId === "no-color"
                            ? null
                            : Number(destination.droppableId);
                        handleCreatedItemChange(idx, {
                          color: { colorId: newCid }
                        });
                      }}
                      onDelete={handleDeleteItem}
                      objects={objects}
                      hiddenItems={hiddenItems}
                      onToggleVisibility={toggleItemVisibility}
                      onAddItemToObject={(objectId) => {
                        const defaultItem = {
                          itemId: nextDraftId(),
                          __isDraft: true,
                          name: "",
                          details: "",
                          material: "",
                          qty: 1,
                          size: "[1,1,18]",
                          position: "[0,0,0]",
                          rotation: "[0,0,0]",
                          color: { colorId: -1 },
                          object: { objectId },
                          work: { workId: +workId }
                        };

                        dispatch(addCreatedItem(defaultItem));
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EditWork;
